import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * MobileNav - Fixed Bottom Tab Bar
 *
 * Bottom navigation bar for mobile devices (<768px).
 * Shows: Browse, Dashboard (auth), Sell (accent), Account
 *
 * Uses fixed positioning with proper safe area handling for
 * iOS Safari and Android Chrome dynamic viewport behavior.
 * The translate3d forces GPU compositing which helps with
 * viewport change synchronization on mobile browsers.
 *
 * Only renders within MainLayout routes. Routes that should hide
 * MobileNav (auth, create, manage) use AuthLayout instead.
 * Desktop hiding handled by md:hidden class.
 */
export default function MobileNav() {
  const { isAuthenticated } = useAuth();

  return (
    <nav
      className="md:hidden fixed left-0 right-0 bottom-0 z-50 bg-midnight border-t border-cream/10"
      style={{
        // Force GPU layer for smoother rendering during viewport changes
        transform: 'translate3d(0, 0, 0)',
        WebkitTransform: 'translate3d(0, 0, 0)',
      }}
    >
      {/* Nav content */}
      <div className="flex items-center justify-around h-16 bg-midnight/95 backdrop-blur-md">
        {/* Browse */}
        <NavItem to="/browse" icon={<BrowseIcon />} label="Browse" />

        {/* Dashboard (auth only) */}
        {isAuthenticated && (
          <NavItem to="/dashboard" icon={<DashboardIcon />} label="Dashboard" />
        )}

        {/* Sell - Accent button */}
        <NavLink
          to="/create"
          className="flex flex-col items-center justify-center -mt-4"
        >
          <div className="w-12 h-12 rounded-full bg-accent text-midnight flex items-center justify-center shadow-lg shadow-accent/30 hover:bg-accent/90 transition-colors">
            <PlusIcon className="w-6 h-6" />
          </div>
          <span className="text-[10px] font-medium text-accent mt-1">Sell</span>
        </NavLink>

        {/* My Entries (auth only) */}
        {isAuthenticated && (
          <NavItem to="/my-entries" icon={<TicketIcon />} label="Entries" />
        )}

        {/* Account / Login */}
        {isAuthenticated ? (
          <NavItem to="/settings" icon={<AccountIcon />} label="Account" />
        ) : (
          <NavItem to="/auth/login" icon={<AccountIcon />} label="Log In" />
        )}
      </div>

      {/* Safe area spacer - fills space below nav for home indicator */}
      <div
        className="bg-midnight"
        style={{ height: 'env(safe-area-inset-bottom, 0px)' }}
      />
    </nav>
  );
}

function NavItem({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center w-16 h-full transition-colors ${
          isActive ? 'text-primary' : 'text-cream/50 hover:text-cream/70'
        }`
      }
    >
      <span className="w-6 h-6">{icon}</span>
      <span className="text-[10px] font-medium mt-1">{label}</span>
    </NavLink>
  );
}

// Icons
function BrowseIcon() {
  return (
    <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
      />
    </svg>
  );
}

function TicketIcon() {
  return (
    <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
      />
    </svg>
  );
}

function AccountIcon() {
  return (
    <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}
